/**
 * Live free-analysis core (user reference clip, 2026-09-12): the browser plays the ORIGINAL
 * upload with the skeleton drawn from the client-pose pose file, dwells on the moments that
 * carry feedback (the P-position stills), and shows the live metric panels beside the clip.
 *
 * RENDER HELPERS ONLY. Every metric (strikes, cadence, knee at strike, joint angles) is
 * computed by the C# analysis pass — the same statics the rendered pills print — and served as
 * the live-series artifact (EL-Golf/LiveSeriesArtifact, GET /uploads/liveSeries). This module
 * pairs frames with playback time, places the clip, and looks artifact values up for display;
 * it never computes a metric (owner rule: no parallel engines — the test allowlist pins it).
 */

// ---- pose file (mirrors NetCoreInterfaces/ClientPoseFile.cs; served by /uploads/clientPose) ----

export interface PoseFileKeypoint {
  landmark_name: string;
  x: number;
  y: number;
  conf?: number;
}

export interface PoseFileFrame {
  keypoints?: PoseFileKeypoint[];
  fileId?: number;
}

export interface LivePoseFile {
  fps: number;
  captureWidth: number;
  captureHeight: number;
  frames: PoseFileFrame[];
}

export interface LivePoint {
  x: number;
  y: number;
  conf: number;
}

/** A frame-keyed pose file laid out over the upload's ORDERED frame list: frames[i] is the pose file
 * frame whose fileId is orderedFrameIds[i], or an empty frame when that split frame carries no
 * pose. Pairing by index (t = start + i/fps) then holds even when the pose file skipped frames
 * (17816 lost its tail to a CDN race). A positional pose file (no fileIds) or an empty list is
 * returned as-is. */
export function alignPoseFileToFrameList(poseFile: LivePoseFile, orderedFrameIds: number[]): LivePoseFile {
  if (orderedFrameIds.length === 0 || poseFile.frames.length === 0) return poseFile;
  if (!poseFile.frames.every((f) => f.fileId != null)) return poseFile;
  const byId = new Map<number, PoseFileFrame>();
  for (const f of poseFile.frames) byId.set(f.fileId as number, f);
  return {
    ...poseFile,
    frames: orderedFrameIds.map((id) => byId.get(id) ?? { fileId: id, keypoints: [] }),
  };
}

/** Per-frame landmark lookup, sentinels dropped (the labeller's rule: -999 or exact (0,0) =
 * untracked, never a coordinate). */
export function framePoints(frame: PoseFileFrame): Map<string, LivePoint> {
  const m = new Map<string, LivePoint>();
  for (const k of frame.keypoints ?? []) {
    if (k.x === -999 || k.y === -999) continue;
    if (k.x === 0 && k.y === 0) continue;
    m.set(k.landmark_name, { x: k.x, y: k.y, conf: k.conf ?? 1 });
  }
  return m;
}

/** The pose file frame paired with playback time t: frames pair by index at the split cadence
 * (t = i/fps from zero — the same contract the labeller aligns on). */
export function frameIndexAt(tSec: number, fps: number, frameCount: number): number {
  if (frameCount <= 0) return 0;
  const i = Math.floor(tSec * fps + 1e-6);
  return Math.min(frameCount - 1, Math.max(0, i));
}

/** The legacy split cadence: what every upload was captured at before the cadence was stored. */
export const LEGACY_FPS = 10;

/** The live view's time base: the upload's STORED capture cadence (freeFeedback.split.fps — the
 * splitter's own value, the single truth), else the pose file's stamp (older API), else the legacy
 * 10. Frame i sits at startSec + i/fps for whichever wins. */
export function captureFpsFor(
  split: { startSec?: number; stopSec?: number; fps?: number } | null | undefined,
  poseFile: { fps?: number } | null | undefined
): number {
  if (split && split.fps != null && split.fps > 0) return split.fps;
  if (poseFile && poseFile.fps != null && poseFile.fps > 0) return poseFile.fps;
  return LEGACY_FPS;
}

/** Whether the C# live-series artifact was computed at this upload's cadence. One computed at
 * another cadence carries WRONG numbers (its cadence and timings assume the other frame
 * interval), so the view must hide it rather than show them. Cadences are compared at the
 * server's "0.###" precision (29.97 written vs 29.970029970 probed agree). */
export function liveSeriesOnCadence(series: { fps?: number } | null | undefined, fps: number): boolean {
  if (!series || series.fps == null || !(series.fps > 0)) return false;
  return Math.abs(series.fps - fps) < 1e-3;
}

// ---- the render stage: ONE geometry across free playback, the stills and the premium video ----

export const STAGE_W = 1280;
export const STAGE_H = 720;

/** The renderer's per-sport player shrink. MUST mirror the C# overrides — the only source of
 * truth is SceneRenderShrink (BaseSetFinder.cs:205 default 1.0; SetFinder_Golf_DTL.cs:669 =
 * 1.15). Pinned by tests so a drift shows up as a red here, not as a misaligned dwell. */
export function sceneRenderShrink(activity: string): number {
  return activity === "golfSwingDTL" ? 1.15 : 1.0;
}

/** Where the renderer places the clip on its 1280x720 canvas (user decision 2026-09-12: unify
 * the FRONT END to the social-media sizing, one output geometry for free and premium). Exact
 * port of BaseSetFinder.ComputeRenderSize + ComputeRenderYOffset for the stills' configuration
 * (full source frame, no comparison zoom-out): aspect-fit into the canvas, divide by the
 * sport's shrink, centre. Integer truncation mirrors the C# casts so the numbers are equal,
 * not merely close. */
/** The render canvas the sport's stills are composed on — KNOWN values, hard-coded (user
 * call, 2026-09-12: no server change, only the free playback flow rescales). Measured on real
 * stills: golf DTL 17808 = 1280x720, run gait 17816 = 1080x1920. The movement family shares
 * the portrait canvas (PROVISIONAL for jump/squat — measured only on gait so far). */
export function stageFor(activity: string): { w: number; h: number } {
  // Every movement and rep family shares the portrait canvas. Each technique's PR added its own
  // clause, so a merge that keeps both sides leaves competing returns here.
  return activity === "runGait"
    || activity.startsWith("verticalJump")
    || activity === "squatLift" || activity === "squatFront" || activity === "squatBehind"
    || activity.startsWith("pushUp")
    || activity.startsWith("pullUp")
    || activity.startsWith("lunge")
    || activity.startsWith("burpee")
    ? { w: 1080, h: 1920 }
    : { w: STAGE_W, h: STAGE_H };
}

export function stageInsetFor(
  captureW: number,
  captureH: number,
  activity: string,
  stageW: number = STAGE_W,
  stageH: number = STAGE_H
): { x: number; y: number; w: number; h: number } {
  const aspect = captureW > 0 && captureH > 0 ? captureW / captureH : 9 / 16;
  let fitW = stageW;
  let fitH = Math.trunc(stageW / aspect);
  if (fitH > stageH) {
    fitH = stageH;
    fitW = Math.trunc(stageH * aspect);
  }
  const shrink = sceneRenderShrink(activity);
  const w = shrink > 1 ? Math.max(1, Math.round(fitW / shrink)) : fitW;
  const h = shrink > 1 ? Math.max(1, Math.round(fitH / shrink)) : fitH;
  return { x: Math.trunc((stageW - w) / 2), y: Math.trunc((stageH - h) / 2), w, h };
}

/** What the renderer publishes next to an upload's stills (EL-Golf/PositionStillArtifact.WriteAll →
 * sfc{id}_stage.json, served in /uploads/freeFeedback as `stage`): the canvas it composed on, the
 * rect it drew the sharp clip into, and the crop it cut from the source as FRACTIONS of the frame.
 * Mirrors BaseSetFinder.TryGetRenderPlacement / TryGetEffectiveCropFractions. */
export interface StageGeometry {
  canvasWidth: number;
  canvasHeight: number;
  placement: { x: number; y: number; width: number; height: number };
  crop: { x: number; y: number; width: number; height: number };
}

export interface PlaybackGeometry {
  canvas: { w: number; h: number };
  placement: { x: number; y: number; w: number; h: number };
  /** Fractions of the source frame; {0,0,1,1} = the whole frame. */
  crop: { x: number; y: number; w: number; h: number };
}

/** The geometry playback uses. The PUBLISHED stage wins — it is what the renderer actually did
 * for this upload, crop included (the body-framing crop is what makes a runner large in the
 * frame; user request 2026-09-12). Without one (older uploads, sports that publish nothing) the
 * sport's known canvas and the ported placement math apply, uncropped — exactly the previous
 * behaviour, so golf and every un-re-rendered upload look the same as before. A malformed stage
 * is treated as absent rather than collapsing the player. */
export function playbackGeometry(
  activity: string,
  captureW: number,
  captureH: number,
  stage: StageGeometry | null | undefined
): PlaybackGeometry {
  const pub = stage && validStage(stage) ? stage : null;
  if (pub) {
    return {
      canvas: { w: pub.canvasWidth, h: pub.canvasHeight },
      placement: { x: pub.placement.x, y: pub.placement.y, w: pub.placement.width, h: pub.placement.height },
      crop: { x: pub.crop.x, y: pub.crop.y, w: pub.crop.width, h: pub.crop.height },
    };
  }
  const canvas = stageFor(activity);
  return {
    canvas,
    placement: stageInsetFor(captureW, captureH, activity, canvas.w, canvas.h),
    crop: { x: 0, y: 0, w: 1, h: 1 },
  };
}

function validStage(s: StageGeometry): boolean {
  const pos = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v > 0;
  const frac = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1;
  return (
    pos(s.canvasWidth) &&
    pos(s.canvasHeight) &&
    !!s.placement &&
    pos(s.placement.width) &&
    pos(s.placement.height) &&
    Number.isFinite(s.placement.x) &&
    Number.isFinite(s.placement.y) &&
    !!s.crop &&
    frac(s.crop.x) &&
    frac(s.crop.y) &&
    pos(s.crop.width) &&
    s.crop.width <= 1 &&
    pos(s.crop.height) &&
    s.crop.height <= 1
  );
}

const pct = (v: number) => `${Math.round(v * 1e4) / 1e4}%`;

/** The sharp window's CSS rect as percentages of the canvas. */
export function placementStyle(
  placement: { x: number; y: number; w: number; h: number },
  canvas: { w: number; h: number }
): { left: string; top: string; width: string; height: string } {
  return {
    left: pct((placement.x / canvas.w) * 100),
    top: pct((placement.y / canvas.h) * 100),
    width: pct((placement.w / canvas.w) * 100),
    height: pct((placement.h / canvas.h) * 100),
  };
}

/** The clip's CSS rect INSIDE the (overflow-hidden) window so the crop fills it: scaled by
 * 1/crop and slid so the crop's origin lands on the window's origin — the same mapping
 * LoadAndSetupImage makes when it resizes the extracted crop into the placement rect. */
export function cropStyle(crop: { x: number; y: number; w: number; h: number }): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  return {
    left: pct((-crop.x / crop.w) * 100),
    top: pct((-crop.y / crop.h) * 100),
    width: pct((1 / crop.w) * 100),
    height: pct((1 / crop.h) * 100),
  };
}

/** Keypoints for a FRACTIONAL frame index: each landmark linearly interpolated between the
 * bracketing pose file frames. Without this the 10 fps keypoints step once per split frame while
 * the clip renders at native rate — the jitter the user flagged (2026-09-12). A landmark
 * present on only one side keeps that side's point rather than popping in and out. */
export function interpolatedFramePoints(
  frames: Array<Map<string, LivePoint>>,
  floatIndex: number
): Map<string, LivePoint> {
  if (frames.length === 0) return new Map();
  const f = Math.min(frames.length - 1, Math.max(0, floatIndex));
  const i0 = Math.floor(f);
  const i1 = Math.min(frames.length - 1, i0 + 1);
  const a = frames[i0] ?? new Map<string, LivePoint>();
  const t = f - i0;
  if (t <= 1e-6 || i0 === i1) return a;
  const b = frames[i1] ?? new Map<string, LivePoint>();
  const out = new Map<string, LivePoint>();
  const names = new Set<string>();
  a.forEach((_, n) => names.add(n));
  b.forEach((_, n) => names.add(n));
  names.forEach((n) => {
    const pa = a.get(n);
    const pb = b.get(n);
    if (pa && pb)
      out.set(n, {
        x: pa.x + (pb.x - pa.x) * t,
        y: pa.y + (pb.y - pa.y) * t,
        conf: Math.min(pa.conf, pb.conf),
      });
    else if (pa) out.set(n, pa);
    else if (pb) out.set(n, pb);
  });
  return out;
}

/** MediaPipe-convention bones for the generic body skeleton (every sport). */
export const SKELETON_BONES: Array<[string, string]> = [
  ["left_shoulder", "right_shoulder"],
  ["left_shoulder", "left_hip"],
  ["right_shoulder", "right_hip"],
  ["left_hip", "right_hip"],
  ["left_shoulder", "left_elbow"],
  ["left_elbow", "left_wrist"],
  ["right_shoulder", "right_elbow"],
  ["right_elbow", "right_wrist"],
  ["left_hip", "left_knee"],
  ["left_knee", "left_ankle"],
  ["left_ankle", "left_heel"],
  ["left_heel", "left_foot_index"],
  ["right_hip", "right_knee"],
  ["right_knee", "right_ankle"],
  ["right_ankle", "right_heel"],
  ["right_heel", "right_foot_index"],
];

// ---- gait math (ports of the server rules — the live numbers must match the pills) ----

// ---- athlete zoom: a client port of the renderer's body-framing crop ----

const BB_LOW_PERCENTILE = 0.05; // BaseSetFinder.BB_LOW_PERCENTILE
const BB_HIGH_PERCENTILE = 0.95; // BaseSetFinder.BB_HIGH_PERCENTILE
const BB_BUFFER_FRACTION = 0.32; // BaseSetFinder.BB_BUFFER_FRACTION (movement sports use the default)

/** BaseSetFinder.Percentile: linear interpolation on a sorted list. */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const rank = p * (sorted.length - 1);
  const idx = Math.floor(rank);
  const frac = rank - idx;
  if (idx + 1 >= sorted.length) return sorted[sorted.length - 1];
  return sorted[idx] + (sorted[idx + 1] - sorted[idx]) * frac;
}

export function isFullFrameCrop(crop: { x: number; y: number; w: number; h: number }): boolean {
  return crop.x === 0 && crop.y === 0 && crop.w === 1 && crop.h === 1;
}

/** The crop (fractions of the source frame) that frames the athlete from the pose file keypoints —
 * BaseSetFinder.ComputeStaticBoundingBox ported: body samples over every frame, 5th–95th
 * percentile extent, centred on the body median, buffered by BB_BUFFER_FRACTION, widened to the
 * target aspect. Deviations, on purpose: the target is the WHOLE canvas (the live player composes
 * no header/caption band), and the athlete is never trimmed — a crop the source cannot hold keeps
 * its size and slides inside the frame, or caps at the frame edge (the placement then aspect-fits).
 * Null with fewer than 5 samples, like the renderer's fallback to the full frame. */
export function athleteCrop(
  frames: Array<Map<string, LivePoint>>,
  sourceW: number,
  sourceH: number,
  canvasW: number,
  canvasH: number,
  /** A rectangle (fractions of the source) the crop must also hold — the rendered labels (owner,
   * 2026-10-12: "athlete zoom please consider that labels must also fit"). */
  include: { x: number; y: number; w: number; h: number } | null = null
): { x: number; y: number; w: number; h: number } | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const f of frames)
    f.forEach((p) => {
      xs.push(p.x);
      ys.push(p.y);
    });
  if (xs.length < 5 || ys.length < 5 || sourceW <= 0 || sourceH <= 0 || canvasW <= 0 || canvasH <= 0) return null;
  xs.sort((a, b) => a - b);
  ys.sort((a, b) => a - b);
  const lowX = percentile(xs, BB_LOW_PERCENTILE);
  const highX = percentile(xs, BB_HIGH_PERCENTILE);
  const lowY = percentile(ys, BB_LOW_PERCENTILE);
  const highY = percentile(ys, BB_HIGH_PERCENTILE);
  // Centred on the EXTENT, not the body median as the renderer does: the median of a standing
  // athlete's 33 landmarks sits in the torso, and a symmetric box around it doubles the longer
  // side — on 17813 (golf DTL) that became the whole frame and zoomed nothing.
  let cx = (lowX + highX) / 2;
  let cy = (lowY + highY) / 2;
  let halfW = ((highX - lowX) / 2) * (1 + BB_BUFFER_FRACTION);
  let halfH = ((highY - lowY) / 2) * (1 + BB_BUFFER_FRACTION);
  // The frame the crop may slide within: the source, grown to wherever the labels reach — the renderer's
  // cards hug the CANVAS's edge, past the clip's own window, and the held still shows that margin.
  let minX = 0, minY = 0, maxX = sourceW, maxY = sourceH;
  if (include && include.w > 0 && include.h > 0) {
    // The buffered body box grown to hold the labels too, then framed as one box.
    const left = Math.min(cx - halfW, include.x * sourceW);
    const right = Math.max(cx + halfW, (include.x + include.w) * sourceW);
    const top = Math.min(cy - halfH, include.y * sourceH);
    const bottom = Math.max(cy + halfH, (include.y + include.h) * sourceH);
    cx = (left + right) / 2;
    cy = (top + bottom) / 2;
    halfW = (right - left) / 2;
    halfH = (bottom - top) / 2;
    minX = Math.min(minX, left); maxX = Math.max(maxX, right);
    minY = Math.min(minY, top); maxY = Math.max(maxY, bottom);
  }
  const target = canvasW / canvasH;
  const bbAspect = halfW / Math.max(1e-6, halfH);
  if (bbAspect < target) halfW = halfH * target;
  else if (bbAspect > target) halfH = halfW / target;
  // Keep the size, slide inside the frame; cap only what the frame cannot hold.
  let w = Math.min(maxX - minX, 2 * halfW);
  let h = Math.min(maxY - minY, 2 * halfH);
  let x = Math.min(Math.max(minX, cx - w / 2), maxX - w);
  let y = Math.min(Math.max(minY, cy - h / 2), maxY - h);
  return { x: x / sourceW, y: y / sourceH, w: w / sourceW, h: h / sourceH };
}

// ---- the rendered labels' extent, so the athlete zoom holds them (owner, 2026-10-12) ----

/** A text card as the renderer draws it (OverlayRenderer: card = true), in CANVAS pixels — the overlay
 * bundle's shapes are in the output canvas's space (BaseSetFinder frameWidth = OutputWidth; measured on
 * 18971: cards at x 680..1034, y 686..1000 of the 1080x1920 still for x 1010 / y 768..960, size 56). The
 * browser has no font metrics for the renderer's typeface, so the text width is an upper estimate. */
const CARD_CHAR_W = 0.6; // of textSize, per character (bold 56 px "Address 33°" measured 0.5)
const CARD_ASCENT = 1.0; // of textSize
const CARD_DESCENT = 0.25;
const CARD_SHADOW = 0.2; // of textSize: the drop shadow (9 px blur at 56 px) on every side

export interface LabelShape {
  shapeType: string;
  shapeKeyValues: Record<string, string>;
}

/** The union of every rendered text card across the bundle, as fractions of the canvas; null when
 * there are none. Only cards: plane lines run to the frame's edges and would undo the zoom. */
export function labelBounds(
  overlays: ReadonlyArray<{ shapes: ReadonlyArray<LabelShape> }> | null | undefined,
  canvasW: number,
  canvasH: number
): { x: number; y: number; w: number; h: number } | null {
  if (!overlays || !(canvasW > 0) || !(canvasH > 0)) return null;
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const ov of overlays)
    for (const sh of ov.shapes ?? []) {
      if (sh.shapeType !== "Text") continue;
      const kv = sh.shapeKeyValues ?? {};
      if (kv.card !== "true" || !kv.text) continue;
      const x = Number(kv.x), y = Number(kv.y), size = Number(kv.textSize);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !(size > 0)) continue;
      const textW = kv.text.length * size * CARD_CHAR_W;
      const padX = size * 0.45;
      const ascent = size * CARD_ASCENT, descent = size * CARD_DESCENT;
      const padY = (ascent + descent) * 0.3;
      const l = kv.align === "right" ? x - textW - padX : kv.align === "center" ? x - textW / 2 - padX : x - padX;
      const shadow = size * CARD_SHADOW;
      left = Math.min(left, l - shadow);
      right = Math.max(right, l + textW + 2 * padX + shadow);
      top = Math.min(top, y - ascent - padY - shadow);
      bottom = Math.max(bottom, y + descent + padY + shadow);
    }
  if (!Number.isFinite(left)) return null;
  const x0 = Math.max(0, left) / canvasW, y0 = Math.max(0, top) / canvasH;
  const x1 = Math.min(canvasW, right) / canvasW, y1 = Math.min(canvasH, bottom) / canvasH;
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** A canvas rectangle (fractions) as fractions of the CLIP's window — the inverse of stillCropFor. NOT clamped
 * to the clip: the renderer's cards hug the canvas's edge, past the window, and the zoom must reach them (the
 * held still shows that margin; the clip shows its backdrop there). Null for a degenerate geometry. */
export function canvasRectToClip(
  g: PlaybackGeometry,
  r: { x: number; y: number; w: number; h: number }
): { x: number; y: number; w: number; h: number } | null {
  const { placement: p, canvas: c } = g;
  if (!(p.w > 0) || !(p.h > 0) || !(r.w > 0) || !(r.h > 0)) return null;
  return { x: (r.x * c.w - p.x) / p.w, y: (r.y * c.h - p.y) / p.h, w: (r.w * c.w) / p.w, h: (r.h * c.h) / p.h };
}

/** The clip's zoom crop, re-expressed against the STILL.
 *
 * The clip and the still are two views of one composition: the clip is a window onto the SOURCE,
 * while the still is the finished CANVAS with that source already placed inside `placement`. So
 * the same zoom is a different rectangle in each, and applying the clip's crop to the still shows
 * the wrong part of it. Zooming the clip while the still kept the whole composition is what the
 * owner saw on 17822 (2026-09-23: "its zoomed well on the playback but then the overlays are
 * compressed") — every held cue frame snapped back to a small athlete.
 *
 * Fractions in, fractions out. A degenerate canvas returns the full frame rather than dividing by
 * zero: a held still that is merely un-zoomed beats one that vanishes. */
export function stillCropFor(
  g: PlaybackGeometry,
  crop: { x: number; y: number; w: number; h: number }
): { x: number; y: number; w: number; h: number } {
  const { placement: p, canvas: c } = g;
  if (!(c.w > 0) || !(c.h > 0) || !(p.w > 0) || !(p.h > 0)) return { x: 0, y: 0, w: 1, h: 1 };
  return {
    x: (p.x + crop.x * p.w) / c.w,
    y: (p.y + crop.y * p.h) / c.h,
    w: (crop.w * p.w) / c.w,
    h: (crop.h * p.h) / c.h,
  };
}

/** The pixel space the ATHLETE ZOOM runs in: the clip's.
 *
 * A pose file's captureWidth/Height are the space its points are in. For the on-device file that is the clip
 * itself; for the GPU file it is the 1280x720 LABEL space (GpuPoseArtifact), whatever the clip's shape. Drawing
 * copes with either — the canvas is stretched into the clip's box, and the label space is that same stretch of
 * the clip — but the zoom frames a rectangle OF THE CLIP, so its aspect must be the clip's. Taking the GPU file's
 * dims for the clip framed a 790x1080 portrait clip as 1280x720 landscape (real 18947, 2026-10-11): a landscape
 * box, the clip letterboxed inside it, the skeleton stretched across the box.
 *
 * The clip's aspect comes from the published placement: with nothing cropped it is the clip aspect-fitted onto the
 * canvas, and the zoom is only offered when nothing was cropped. Truncating that fit to whole pixels moves its
 * aspect by under 0.1% (939/1283 vs 790/1080), so a capture within 1% of it IS the clip (the on-device file) and
 * stands as it is; otherwise the pose file's height is kept and its width set by the placement, so the points
 * rescale onto it (toClipSpace). Without a usable stage the capture space stands, as before. */
export function clipPixelSpace(
  stage: StageGeometry | null | undefined,
  captureW: number,
  captureH: number
): { w: number; h: number } {
  const capture = { w: captureW, h: captureH };
  const pub = stage && validStage(stage) ? stage : null;
  if (!pub || captureW <= 0 || captureH <= 0) return capture;
  if (!isFullFrameCrop({ x: pub.crop.x, y: pub.crop.y, w: pub.crop.width, h: pub.crop.height })) return capture;
  const clipAspect = pub.placement.width / pub.placement.height;
  if (Math.abs(captureW / captureH / clipAspect - 1) < 0.01) return capture;
  return { w: captureH * clipAspect, h: captureH };
}

/** The pose file's points rescaled from its capture space onto `clip` (clipPixelSpace). The same frames back when
 * the spaces are the same, so the on-device file costs nothing. */
export function toClipSpace(
  frames: Array<Map<string, LivePoint>>,
  captureW: number,
  captureH: number,
  clip: { w: number; h: number }
): Array<Map<string, LivePoint>> {
  const sx = clip.w / captureW;
  const sy = clip.h / captureH;
  if (sx === 1 && sy === 1) return frames;
  return frames.map((f) => {
    const m = new Map<string, LivePoint>();
    f.forEach((pt, name) => m.set(name, { ...pt, x: pt.x * sx, y: pt.y * sy }));
    return m;
  });
}

/** The geometry with a client crop applied: the crop's pixel aspect is aspect-fitted into the
 * canvas and centred (ComputeRenderSize/ComputeRenderYOffset, no sport shrink), exactly as the
 * renderer places its own cropped bitmap. */
export function zoomedGeometry(
  g: PlaybackGeometry,
  crop: { x: number; y: number; w: number; h: number },
  sourceW: number,
  sourceH: number
): PlaybackGeometry {
  // The crop's aspect is in SOURCE pixels (fractions × the clip size), never the canvas's.
  const aspect = (crop.w * sourceW) / Math.max(1e-6, crop.h * sourceH);
  let fitW = g.canvas.w;
  let fitH = Math.trunc(g.canvas.w / aspect);
  if (fitH > g.canvas.h) {
    fitH = g.canvas.h;
    fitW = Math.trunc(g.canvas.h * aspect);
  }
  return {
    canvas: g.canvas,
    placement: { x: Math.trunc((g.canvas.w - fitW) / 2), y: Math.trunc((g.canvas.h - fitH) / 2), w: fitW, h: fitH },
    crop,
  };
}

// ---- the live-series artifact: what the C# analysis pass computed (mirrors EL-Golf/LiveSeries) ----

export interface CadenceSample {
  frame: number;
  t: number;
  spm: number;
}

export interface AngleSample {
  frame: number;
  t: number;
  deg: number;
}

export interface LiveSeries {
  fps: number;
  /** The first split frame id: index = frame - minFrame pairs a sample with the frame list. */
  minFrame: number;
  /** Right-foot strikes (split frame ids). */
  strikes: number[];
  /** Left-foot strikes, the same detector on the left ankle; absent on older artifacts. */
  leftStrikes?: number[];
  cadenceSpm: number | null;
  cadenceSeries: CadenceSample[];
  kneeAtStrike: { meanDeg: number; spreadDeg: number; perStrike: AngleSample[] } | null;
  /** side ("left" | "right") → joint ("knee" | "hip" | "elbow" | "shoulder") → samples. */
  jointAngles: Record<string, Record<string, AngleSample[]>>;
}

/** The artifact sample on exactly this split-frame index, or null — a display lookup, never a
 * neighbour's value or an interpolation. */
export function sampleAtIndex<T extends { frame: number }>(samples: T[], minFrame: number, index: number): T | null {
  for (const s of samples) if (s.frame - minFrame === index) return s;
  return null;
}

/** Artifact samples in the page's chart shape (MetricChartData), x = split-frame index. */
export function seriesChart<T extends { frame: number }>(
  title: string,
  unit: string,
  samples: T[],
  minFrame: number,
  value: (s: T) => number,
  source: "sequenceMeasures" | "positionMeasures" = "sequenceMeasures"
): { title: string; unit: string; dataPoints: { frameIndex: number; value: number }[]; min: number; max: number; avg: number; source: "sequenceMeasures" | "positionMeasures" } {
  const values = samples.map(value);
  return {
    title,
    unit,
    dataPoints: samples.map((s) => ({ frameIndex: s.frame - minFrame, value: value(s) })),
    min: values.length ? Math.min(...values) : 0,
    max: values.length ? Math.max(...values) : 0,
    avg: values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0,
    source,
  };
}

export interface DwellMoment {
  label: string;
  timeSec: number;
  fileId: number;
  stillUrl: string | null;
}

/** The clip's feedback moments: each resolved P-position mapped to playback time via its
 * frame's INDEX in the upload's ordered frame list (frames pair by index at the split
 * cadence). Positions whose frame is unknown are dropped, order is chronological. */
export function dwellMomentsFrom(
  positions: Array<{ label: string; fileId: number | null }>,
  orderedFrameIds: number[],
  fps: number,
  stillUrlFor: (label: string) => string | null,
  /** The split window's start (freeFeedback.split.startSec): frame i plays at start + i/fps. */
  startSec = 0
): DwellMoment[] {
  const out: DwellMoment[] = [];
  for (const p of positions) {
    if (p.fileId == null || p.fileId <= 0) continue;
    const idx = orderedFrameIds.indexOf(p.fileId);
    if (idx < 0) continue;
    out.push({
      label: p.label,
      timeSec: startSec + idx / fps,
      fileId: p.fileId,
      stillUrl: stillUrlFor(p.label),
    });
  }
  out.sort((a, b) => a.timeSec - b.timeSec);
  return out;
}

// ---- Foot-strike pulse and trail fade (user, 2026-09-19: the reference clip's ring that
// pulses on the ankle at each strike and its ankle path that fades along its length).
// RENDER ONLY: the strikes are the C# pass's; the browser only decides how to draw them.

/** How long a strike's ring lives after the strike frame, in seconds (user, 2026-09-19: "it
 * lingers too long" at 0.35). PROVISIONAL. */
export const PULSE_SECONDS = 0.1;

/** How far ahead of the strike frame the ring starts (user, 2026-09-19: "it should appear as
 * the foot strikes, not after", and 0.1 was still "too late"). The detector marks the foot-flat
 * frame, after the landing the eye sees. PROVISIONAL. */
export const PULSE_LEAD_SECONDS = 0.3;

/** The strikes currently pulsing at a playback position: `strikeIndices` are split-frame indices
 * (frame − minFrame), `floatIdx` the interpolated frame position, `fps` the pose cadence.
 * progress runs 0 at PULSE_LEAD_SECONDS before the strike to 1 at PULSE_SECONDS after it. */
export function activeStrikePulses(
  strikeIndices: number[],
  floatIdx: number,
  fps: number,
): Array<{ index: number; progress: number }> {
  const lead = PULSE_LEAD_SECONDS * fps;
  const span = lead + PULSE_SECONDS * fps;
  const out: Array<{ index: number; progress: number }> = [];
  for (const index of strikeIndices) {
    const age = floatIdx - (index - lead);
    if (age < 0 || age > span) continue;
    out.push({ index, progress: span > 0 ? age / span : 1 });
  }
  return out;
}

/** The ring at a pulse's progress: tight, bright and thick at the strike; wide, transparent and
 * thin at the end. Radius in canvas pixels. */
export function pulseRing(progress: number): { radius: number; alpha: number; width: number } {
  const p = Math.min(1, Math.max(0, progress));
  const ease = 1 - (1 - p) * (1 - p); // fast out, settling
  return { radius: 18 + 60 * ease, alpha: 0.95 * (1 - p), width: 5.5 - 3 * p };
}

/** Trail opacity by age: 0 = the oldest point of the trail, 1 = the current one. */
/** The trail window, in seconds: ONE stride of the cadence the C# pass published. A stride is two
 * steps, so it is 120 / spm. Nothing is derived here — cadenceSpm is the analysis's own number.
 *
 * Why the window matters (user, 2026-09-19: "17820 is a very strong cadence of running"): that
 * upload turns over at 204 spm, so the fixed four-second trail drew 6.7 strides of ankle path over
 * each other and read as a tangle, which is why the glow had to be kept faint. One stride draws one
 * loop, and because a stride repeats, the loop holds its shape from step to step — consistent, and
 * therefore able to carry a much stronger glow. Null when no cadence: the caller keeps the fixed
 * window, which is right for a clip with no strikes at all. */
export function strideSeconds(series: { cadenceSpm?: number | null } | null | undefined): number | null {
  const spm = series?.cadenceSpm;
  return typeof spm === "number" && spm > 0 ? 120 / spm : null;
}

/** The outline of a tapered ribbon along pts: one closed path, full width at the HEAD (the foot
 * now) tapering to a point at the tail. Returned head-last down one side and tail-last back up the
 * other, so the caller fills it in one go with a single gradient and a single shadow.
 *
 * It replaces one short stroke per frame, each carrying its own halo: those halos stacked into the
 * patchy look, and the stack is also why a strong glow filled the frame. Measured in Chromium, one
 * filled path costs 0.04 ms a frame against 1.93 ms for 120 haloed strokes. `taper` shapes how fast
 * the width falls away (1 = linear); both it and the head width are look values, PROVISIONAL. */
export function ribbonOutline(
  pts: Array<{ x: number; y: number }>,
  headWidth: number,
  taper: number
): Array<{ x: number; y: number }> {
  const n = pts.length - 1;
  if (n < 1) return [];
  const near: Array<{ x: number; y: number }> = [];
  const far: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= n; i++) {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(n, i + 1)];
    let dx = b.x - a.x;
    let dy = b.y - a.y;
    const m = Math.hypot(dx, dy) || 1;
    dx /= m;
    dy /= m;
    const half = (headWidth * Math.pow(i / n, taper)) / 2;
    near.push({ x: pts[i].x - dy * half, y: pts[i].y + dx * half });
    far.push({ x: pts[i].x + dy * half, y: pts[i].y - dx * half });
  }
  far.reverse();
  return near.concat(far);
}


